"use client";

import { ArrowLeft, FileText } from "lucide-react";
import Link from "next/link";

import { PageEyebrow } from "@/components/public/page-eyebrow";
import {
  PUBLIC_CONTAINER,
  PUBLIC_PANEL_RADIUS,
  PUBLIC_SECTION_PY_CONTENT,
} from "@/components/public/public-layout-styles";
import { cn } from "@/lib/utils";

interface TermsContentProps {
  lastUpdated: string;
}

export function TermsContent({ lastUpdated }: TermsContentProps) {
  return (
    <main className="relative z-10">
      <div className={cn(PUBLIC_CONTAINER, "max-w-4xl", PUBLIC_SECTION_PY_CONTENT)}>
        {/* Back Link */}
        <Link
          className="text-muted-foreground hover:text-foreground mb-8 inline-flex items-center gap-2 text-sm transition-colors"
          href="/"
        >
          <ArrowLeft className="size-4" />
          Voltar ao início
        </Link>

        {/* Page Header */}
        <div className="mb-12">
          <PageEyebrow className="mb-4" icon={FileText}>
            Termos Legais
          </PageEyebrow>
          <h1 className="mb-4 text-4xl font-semibold tracking-tight">Termos de uso</h1>
          <p className="text-muted-foreground">Última atualização: {lastUpdated}</p>
        </div>

        {/* Content Card */}
        <div
          className={cn("border-border/50 bg-card border p-6 sm:p-8 lg:p-10", PUBLIC_PANEL_RADIUS)}
        >
          <div className="prose max-w-none">
            <p>
              Bem-vindo ao <strong>AgroProj</strong>, plataforma operada pela{" "}
              <strong>AGROSISTEMAS SOFTWARES AGRO LTDA</strong>, pessoa jurídica de direito privado,
              inscrita no CNPJ sob o nº 51.145.228/0001-00, com sede na Rua Pelotas, 19, sala 01B,
              Centro, Machadinho - RS, CEP 99880-000, Brasil. Ao acessar ou usar nossa plataforma,
              você concorda com estes Termos de Uso. Leia-os atentamente antes de utilizar nossos
              serviços.
            </p>

            <h2>1. Definições</h2>
            <ul>
              <li>
                <strong>"Plataforma"</strong>: refere-se ao sistema AgroProj, incluindo website,
                aplicativos e serviços relacionados
              </li>
              <li>
                <strong>"Usuário"</strong>: pessoa física ou jurídica que utiliza a Plataforma
              </li>
              <li>
                <strong>"Serviços"</strong>: funcionalidades oferecidas pela Plataforma, incluindo
                gestão de produtores, projetos agrícolas e relatórios de investimento
              </li>
              <li>
                <strong>"Conteúdo"</strong>: dados, informações e documentos inseridos na Plataforma
              </li>
            </ul>

            <h2>2. Aceitação dos Termos</h2>
            <p>
              Ao criar uma conta ou utilizar a Plataforma, você declara ter lido, compreendido e
              aceito estes Termos de Uso e nossa{" "}
              <Link className="text-primary hover:underline" href="/privacy">
                Política de Privacidade
              </Link>
              . Se você não concordar com algum termo, não utilize nossos serviços.
            </p>

            <h2>3. Cadastro e Conta</h2>
            <h3>3.1 Requisitos</h3>
            <ul>
              <li>Você deve ter pelo menos 18 anos de idade</li>
              <li>Fornecer informações verdadeiras, completas e atualizadas</li>
              <li>Manter a confidencialidade de suas credenciais de acesso</li>
            </ul>

            <h3>3.2 Responsabilidades</h3>
            <ul>
              <li>Você é responsável por todas as atividades realizadas em sua conta</li>
              <li>Notifique-nos imediatamente sobre qualquer uso não autorizado</li>
              <li>Não compartilhe suas credenciais com terceiros não autorizados</li>
            </ul>

            <h2>4. Uso da Plataforma</h2>
            <h3>4.1 Uso Permitido</h3>
            <p>A Plataforma deve ser utilizada para:</p>
            <ul>
              <li>Gestão de produtores rurais e propriedades agrícolas</li>
              <li>Elaboração de projetos de investimento e financiamento rural</li>
              <li>Geração de relatórios técnicos e financeiros</li>
              <li>Gestão de relacionamento com clientes (CRM)</li>
              <li>Controle financeiro relacionado à atividade agrícola</li>
            </ul>

            <h3>4.2 Uso Proibido</h3>
            <p>É expressamente proibido:</p>
            <ul>
              <li>Utilizar a Plataforma para fins ilegais ou não autorizados</li>
              <li>Inserir informações falsas ou fraudulentas</li>
              <li>Tentar acessar áreas restritas ou sistemas não autorizados</li>
              <li>Realizar engenharia reversa, descompilar ou modificar o software</li>
              <li>Utilizar robôs, scrapers ou métodos automatizados não autorizados</li>
              <li>Transmitir vírus, malware ou código malicioso</li>
              <li>Violar direitos de propriedade intelectual</li>
            </ul>

            <h2>5. Conteúdo do Usuário</h2>
            <h3>5.1 Propriedade</h3>
            <p>
              Você mantém a propriedade de todo o Conteúdo que inserir na Plataforma. Ao utilizar
              nossos serviços, você nos concede uma licença limitada para processar, armazenar e
              exibir seu Conteúdo conforme necessário para a prestação dos serviços.
            </p>

            <h3>5.2 Responsabilidade</h3>
            <p>Você é o único responsável pelo Conteúdo inserido, garantindo que:</p>
            <ul>
              <li>Possui os direitos necessários para utilizar tais informações</li>
              <li>As informações são precisas e verdadeiras</li>
              <li>Não viola direitos de terceiros</li>
              <li>Está em conformidade com a legislação aplicável</li>
            </ul>

            <h2>6. Propriedade Intelectual</h2>
            <p>
              A Plataforma, incluindo seu código-fonte, design, marcas, logotipos e conteúdo
              original, são de propriedade exclusiva do AgroProj ou de seus licenciadores. É
              proibida a reprodução, distribuição ou modificação sem autorização prévia por escrito.
            </p>

            <h2>7. Pagamento e Assinatura</h2>
            <h3>7.1 Planos</h3>
            <p>
              Os serviços podem ser oferecidos em diferentes planos, gratuitos ou pagos. Os detalhes
              de cada plano, incluindo funcionalidades e preços, estão disponíveis na Plataforma.
            </p>

            <h3>7.2 Cobrança</h3>
            <ul>
              <li>Os valores são cobrados conforme o plano escolhido (mensal ou anual)</li>
              <li>A renovação é automática, salvo cancelamento prévio</li>
              <li>Alterações de preço serão comunicadas com antecedência mínima de 30 dias</li>
            </ul>

            <h3>7.3 Reembolso</h3>
            <p>
              Solicitações de reembolso serão analisadas conforme nossa política de reembolso,
              disponível mediante solicitação ao suporte.
            </p>

            <h2>8. Disponibilidade e Suporte</h2>
            <p>
              Nos esforçamos para manter a Plataforma disponível 24 horas por dia, 7 dias por
              semana. No entanto, podemos realizar manutenções programadas ou enfrentar
              indisponibilidades técnicas. Não garantimos disponibilidade ininterrupta.
            </p>

            <h2>9. Limitação de Responsabilidade</h2>
            <p>Na extensão máxima permitida por lei:</p>
            <ul>
              <li>A Plataforma é fornecida "como está", sem garantias de qualquer tipo</li>
              <li>
                Não nos responsabilizamos por decisões tomadas com base em relatórios ou informações
                geradas pela Plataforma
              </li>
              <li>Não somos responsáveis por danos indiretos, incidentais ou consequenciais</li>
              <li>
                Nossa responsabilidade total está limitada ao valor pago pelo Usuário nos últimos 12
                meses
              </li>
            </ul>

            <h2>10. Isenção de Responsabilidade - Projetos de Financiamento</h2>
            <p>
              A Plataforma auxilia na elaboração de projetos de investimento e financiamento rural,
              mas:
            </p>
            <ul>
              <li>
                <strong>Não garantimos</strong> a aprovação de financiamentos ou créditos junto a
                instituições financeiras
              </li>
              <li>
                Os relatórios gerados são ferramentas de apoio e devem ser revisados por
                profissionais qualificados
              </li>
              <li>O Usuário é responsável pela veracidade das informações fornecidas</li>
              <li>
                Recomendamos sempre consultar um profissional técnico habilitado (engenheiro
                agrônomo, técnico agrícola, etc.)
              </li>
            </ul>

            <h2>11. Rescisão</h2>
            <h3>11.1 Pelo Usuário</h3>
            <p>
              Você pode encerrar sua conta a qualquer momento através das configurações da
              Plataforma ou entrando em contato com nosso suporte.
            </p>

            <h3>11.2 Pelo AgroProj</h3>
            <p>Podemos suspender ou encerrar seu acesso em caso de:</p>
            <ul>
              <li>Violação destes Termos de Uso</li>
              <li>Conduta fraudulenta ou ilegal</li>
              <li>Inadimplência de pagamentos</li>
              <li>Solicitação de autoridades competentes</li>
            </ul>

            <h3>11.3 Efeitos da Rescisão</h3>
            <p>
              Após o encerramento, você poderá exportar seus dados por um período de 30 dias. Após
              esse prazo, os dados poderão ser excluídos conforme nossa Política de Privacidade.
            </p>

            <h2>12. Alterações nos Termos</h2>
            <p>
              Podemos modificar estes Termos a qualquer momento. Alterações significativas serão
              comunicadas por e-mail ou através de aviso na Plataforma com antecedência mínima de 30
              dias. O uso continuado após as alterações constitui aceitação dos novos termos.
            </p>

            <h2>13. Disposições Gerais</h2>
            <ul>
              <li>
                <strong>Legislação aplicável:</strong> Estes Termos são regidos pelas leis da
                República Federativa do Brasil
              </li>
              <li>
                <strong>Foro:</strong> Fica eleito o foro da comarca de Machadinho/RS para dirimir
                quaisquer controvérsias
              </li>
              <li>
                <strong>Acordo integral:</strong> Estes Termos constituem o acordo integral entre as
                partes
              </li>
              <li>
                <strong>Cessão:</strong> Você não pode ceder seus direitos ou obrigações sem nosso
                consentimento prévio
              </li>
            </ul>

            <h2>14. Contato</h2>
            <p>Para dúvidas ou esclarecimentos sobre estes Termos:</p>
            <ul>
              <li>
                <strong>E-mail:</strong> contato@agroproj.com
              </li>
              <li>
                <strong>Website:</strong> agroproj.com
              </li>
              <li>
                <strong>Endereço:</strong> Rua Pelotas, 19, sala 01B, Centro, Machadinho - RS, CEP
                99880-000, Brasil
              </li>
            </ul>
          </div>

          {/* Legal Note */}
          <div className={cn("border-border/50 bg-surface mt-10 border p-4", PUBLIC_PANEL_RADIUS)}>
            <p className="text-muted-foreground text-sm">
              Ao utilizar o AgroProj, você confirma que leu, compreendeu e concorda com estes Termos
              de Uso.
            </p>
          </div>
        </div>

        {/* Navigation Links */}
        <div className="mt-8 flex flex-wrap items-center gap-4">
          <Link
            className="text-primary hover:text-primary/80 text-sm transition-colors"
            href="/privacy"
          >
            Política de privacidade →
          </Link>
        </div>
      </div>
    </main>
  );
}
